
    import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
    import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
    import Beleg from '@/types/Beleg';
    import Belegposition from '@/types/Belegposition';
    import DialogDruckOk from '@/components/Impfverwaltung/Rechnung/DialogDruckOk.vue';
    import DialogAddAgainToWarteliste from '@/components/Impfverwaltung/Rechnung/DialogAddAgainToWarteliste.vue';
    import Patient from '@/types/Patient';
    import { Tarifart } from '@/types/Tarifart';
    import { NeueRechnungPayload, UpdateRechnungPayload } from '@/store/patientrechnungen';
    import { warningSwal } from '@/helper/swals';
    import Warteliste from '@/types/Warteliste';
    import { ListItems, VForm, VTextField } from '@/types/Components';
    import { DataTableHeaders, MutableDataTableHeaders } from '@/types';
    import { SystemEinstellung, SystemEinstellungKey } from '@/types/Einstellung';

    @Component({
        components: {
            DialogDruckOk,
            DialogAddAgainToWarteliste,
        },
        computed: {
            ...mapState('globals', ['rules']),
            ...mapState('patient', [
                'patientData',
                'beleg',
                'belegPositionen',
                'belegPatienten',
                'druckvorgang',
                'abbrechungsvorgang',
                'dialogs',
                'loadRechnung',
                'abbrechungsvorgangAlle',
                'zahlendePatienten',
            ]),
            ...mapState('zahlungsarten', ['arten']),
            ...mapState('zahlungsarten', { zahlungsartenLoaders: 'loaders' }),
            ...mapGetters('authentication', ['impfstelleDetail', 'getSystemEinstellung']),
            ...mapGetters('patient', ['getOffeneImpfungen']),
            ...mapGetters('leistungen', ['getVerwaltungsabgabe']),
            ...mapState('warteliste', ['warteliste']),
        },
        methods: {
            ...mapActions('patient', ['neueRechnung', 'updateAbrechnung', 'rechnungAbbrechen', 'resetRechnungsdruck']),
            ...mapMutations('patient', ['setDialogRechnung', 'setAddAgainToWartelisteDialog']),
            ...mapActions('zahlungsarten', ['loadZahlungsarten']),
        },
    })
    export default class Rechnung extends Vue {
        @Prop() visible!: boolean;

        public $refs!: {
            form: VForm;
            diagnose: VTextField;
        };

        public hcw_einschliessen = 'n';

        public beleg!: Beleg;
        public patientData!: Patient | null;
        public belegPositionen!: Belegposition[];
        public belegPatienten!: Patient[];
        public zahlendePatienten!: Warteliste[];
        public warteliste!: Warteliste[];
        public loadZahlungsarten!: () => void;
        public rechnungAbbrechen!: () => void;
        public neueRechnung!: (payload: NeueRechnungPayload) => void;
        public updateAbrechnung!: (payload: UpdateRechnungPayload) => void;
        public getSystemEinstellung!: <T>(parameter: SystemEinstellungKey) => SystemEinstellung<T>;
        public resetRechnungsdruck!: () => void;

        public patienten: Warteliste[] = [];
        public selectedPatienten: Warteliste[] = [];
        public items: Belegposition[] = [];

        public readonly tarife: ListItems<Tarifart> = [
            { value: Tarifart.Normal, text: 'Normaltarif' },
            { value: Tarifart.Individuell, text: 'Individualtarif' },
            { value: Tarifart.KeineVerrechnung, text: 'keine Verrechnung' },
            { value: Tarifart.Spaeter, text: 'später verrechnen' },
            { value: Tarifart.Rabatt5, text: '5% Rabatt' },
            { value: Tarifart.Rabatt10, text: '10% Rabatt' },
            { value: Tarifart.Rabatt15, text: '15% Rabatt' },
            { value: Tarifart.Rabatt20, text: '20% Rabatt' },
            { value: Tarifart.Rabatt30, text: '30% Rabatt' },
        ];

        public currentBeleg = new Beleg();
        public rechnungsempfaenger = new Patient();
        public erstordination = true;

        public get headers(): DataTableHeaders<Warteliste, 'patient' | 'action'> {
            const headers: MutableDataTableHeaders<Warteliste, 'patient' | 'action'> = this.showSelect
                ? [{ value: 'action', text: '', sortable: false, width: 40 }]
                : [];

            headers.push({ value: 'patient', text: 'Rechnung wird für folgende Patienten erstellt:', sortable: false });

            return headers;
        }

        public get showSelect(): boolean {
            return !this.onlySinglePatient && !this.hasPositionen;
        }

        public get hasPositionen(): boolean {
            return this.belegPositionen.length > 0;
        }

        public get onlySinglePatient(): boolean {
            return this.patienten.length === 1;
        }

        public get selectedZahlendePatienten(): boolean {
            return this.zahlendePatienten.length > 0;
        }

        public get tempRechnungsEmpfaenger(): Warteliste | undefined {
            const [rechnungsEmpfaenger] = this.selectedPatienten;
            return rechnungsEmpfaenger;
        }

        @Watch('visible')
        public onChangeVisible(visible: boolean): void {
            if (visible) {
                if (this.$refs.form) {
                    this.$refs.form.resetValidation();
                }
                if (this.$refs.diagnose) {
                    this.$refs.diagnose.resetValidation();
                }

                this.hcw_einschliessen = 'n';

                this.currentBeleg = new Beleg();
                this.items = [];
                this.selectedPatienten = [];
                this.erstordination = true;

                this.loadZahlungsarten();

                const isSelected = this.selectedZahlendePatienten;

                this.patienten = isSelected
                    ? [...this.zahlendePatienten]
                    : [...this.warteliste];

                if (this.onlySinglePatient) {
                    this.selectedPatienten = [...this.patienten];
                }
            }
        }

        @Watch('belegPositionen')
        public onChangeBelegpositionen(positionen: Belegposition[]): void {
            if (positionen.length === 0) {
                return;
            }

            const zahlungsart = this.currentBeleg.zahlungsart;
            const diagnose = this.currentBeleg.diagnose;

            this.currentBeleg = new Beleg(this.beleg);
            this.currentBeleg.zahlungsart = zahlungsart;
            this.currentBeleg.diagnose = diagnose;
            this.items = positionen;
            this.selectedPatienten = [];

            const rechnungsempfaenger = this.belegPatienten.find(({ patnr }) => patnr === this.currentBeleg.patnr);
            if (rechnungsempfaenger) {
                this.rechnungsempfaenger = new Patient(rechnungsempfaenger);

                if (this.rechnungsempfaenger.rech_adrzeile1?.trim()) {
                    this.onClickRechnungsadresse();
                } else {
                    this.onClickPatientenadresse();
                }
            }
        }

        public onClickPatientenadresse(): void {
            if (this.hasPositionen && this.rechnungsempfaenger.patnr) {
                Object.assign(this.currentBeleg, {
                    name: this.rechnungsempfaenger.name,
                    adresszeile1: this.rechnungsempfaenger.name,
                    adresszeile2: this.rechnungsempfaenger.strasse ?? '',
                    adresszeile3: [this.rechnungsempfaenger.plz, this.rechnungsempfaenger.ort]
                        .filter((str) => str)
                        .join(' '),
                    adresszeile4: this.rechnungsempfaenger.land_bezeichnung ?? '',
                });
            }
        }

        public onClickRechnungsadresse(): void {
            if (this.hasPositionen && this.rechnungsempfaenger.patnr) {
                const zeilen = [
                    this.rechnungsempfaenger.rech_adrzeile1,
                    this.rechnungsempfaenger.rech_adrzeile2,
                    this.rechnungsempfaenger.rech_adrzeile3,
                ];

                const plzOrt = [
                    this.rechnungsempfaenger.rech_plz,
                    this.rechnungsempfaenger.rech_ort,
                ];

                Object.assign(this.currentBeleg, {
                    name: this.rechnungsempfaenger.name,
                    adresszeile1: zeilen.filter((str) => str).join('\n'),
                    adresszeile2: this.rechnungsempfaenger.rech_strasse ?? '',
                    adresszeile3: plzOrt.filter((str) => str).join(' '),
                    adresszeile4: this.rechnungsempfaenger.rech_land_bezeichnung ?? '',
                });
            }
        }

        public calcPreis(item: Belegposition): number {
            if (item.tarifart === Tarifart.Individuell) {
                item.verkaufspreis_brutto = item.temp_verkaufspreis || 0;
            } else {
                if (item.isImpfung) {
                    if (!this.getSystemEinstellung('muw').isEnabled()) {
                        item.verkaufspreis_brutto = item.impfstoff_verkaufspreis ?? 0;
                    }
                } else if (item.isLeistung) {
                    if (!this.getSystemEinstellung('muw').isEnabled()) {
                        item.verkaufspreis_brutto = item.leistung_preis ?? 0;
                    }
                } else if (item.isUntersuchung) {
                    if (!this.getSystemEinstellung('muw').isEnabled()) {
                        item.verkaufspreis_brutto = item.untersuchung_preis ?? 0;
                    }
                } else {
                    item.verkaufspreis_brutto = 0;
                }
            }

            const rabatt = ((): number => {
                switch (item.tarifart) {
                    case Tarifart.Rabatt5:
                        return 0.95;
                    case Tarifart.Rabatt10:
                        return 0.9;
                    case Tarifart.Rabatt15:
                        return 0.85;
                    case Tarifart.Rabatt20:
                        return 0.8;
                    case Tarifart.Rabatt30:
                        return 0.7;
                    case Tarifart.Spaeter:
                        return 0;
                    case Tarifart.KeineVerrechnung:
                        return 0;
                    default:
                        return 1;
                }
            })();

            //const round = (betrag: number): number => Math.round(betrag * 100) / 100;
            const round = (betrag: number): number => Number(Number.parseFloat(String(betrag)).toFixed(2));

            item.anzahl = item.anzahl ?? 1;
            if (item.verkaufspreis_brutto) {
                item.betrag_brutto = round(item.verkaufspreis_brutto * item.anzahl * rabatt);
            } else {
                item.betrag_brutto = 0;
            }
            item.steuersatz = item.steuersatz ?? 0;
            if (item.verkaufspreis_brutto) {
                item.verkaufspreis = round((item.verkaufspreis_brutto / (1 + (item.steuersatz / 100))) * rabatt);
            } else {
                item.verkaufspreis = 0;
            }
            item.betrag = item.verkaufspreis * item.anzahl;
            item.steuer = item.betrag_brutto - item.betrag;

            return item.betrag_brutto;
        }

        public sumBetrag(sum: number, pos: Belegposition): number {
            return sum += this.calcPreis(pos);
        }

        public sumBetragBrutto(sum: number, pos: Belegposition): number {
            return sum += pos.betrag_brutto ?? 0;
        }

        public sumBetragNetto(sum: number, pos: Belegposition): number {
            return sum += pos.betrag ?? 0;
        }

        public getSum(): number {
            const sum = this.items.reduce(this.sumBetrag, 0);
            this.currentBeleg.gesamtbetrag = sum;
            return sum;
        }

        public calcBruttoNetto(mehrwertsteuer: number): { brutto: number; netto: number; mwst: number } {
            if (mehrwertsteuer === 0) {
                const brutto = this.items
                    .filter(({ steuersatz }) => ![10, 20].includes(steuersatz ?? 0))
                    .reduce(this.sumBetragBrutto, 0);

                return {
                    brutto,
                    netto: brutto,
                    mwst: 0,
                };
            }

            const brutto = this.items
                .filter(({ steuersatz }) => steuersatz === mehrwertsteuer)
                .reduce(this.sumBetragBrutto, 0);

            const netto = this.items
                .filter(({ steuersatz }) => steuersatz === mehrwertsteuer)
                .reduce(this.sumBetragNetto, 0);

            return {
                brutto,
                netto,
                mwst: brutto - netto,
            };
        }

        public onClickCreate(): void {
            if (!this.hasPositionen && this.selectedPatienten.length > 0) {
                this.neueRechnung({
                    patienten: this.patienten.map(({ patnr }) => patnr as number),
                    erstordination: this.erstordination,
                    rechnungsempfaenger: this.tempRechnungsEmpfaenger?.patnr as number,
                    hcw_einschliessen: this.hcw_einschliessen
                });
            }
        }

        public onClickCancel(): void {
            if (this.hasPositionen) {
                this.rechnungAbbrechen();
            } else {
                this.$emit('click:cancel');
            }
        }

        public onClickPrint(): void {
            if (!this.$refs.form.validate() || !this.$refs.diagnose.validate() || !this.hasPositionen) {
                return;
            }

            if (!this.currentBeleg.patnr) {
                warningSwal('Warnung', 'Bitte wählen Sie einen Rechnungsempfänger aus.');
                return;
            }

            this.updateAbrechnung({
                beleg: this.currentBeleg,
                belegpositionen: this.items,
                count: this.getSystemEinstellung('rechnungsdruck_doppelt').isEnabled() ? 2 : 1,
                druckenUndAbschliessen: this.getSystemEinstellung('rechnung_drucken_und_abschliessen').isEnabled(),
            });
        }

        public onClickCancelDruckOk(): void {
            this.resetRechnungsdruck();
        }
    }
